"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import type { Map as LeafletMap } from "leaflet";
import type { FoodCustomerMapRequest } from "@/lib/foodCustomerMap";

type Props = { map: LeafletMap | null; onClose: () => void };
const dateFormat = new Intl.DateTimeFormat("en-IN", {
  timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
});

export default function FoodCustomersPanel({ map, onClose }: Props) {
  const [requests, setRequests] = useState<FoodCustomerMapRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [needsLogin, setNeedsLogin] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [refresh, setRefresh] = useState(0);
  const selected = requests.find((item) => item.id === selectedId);
  const rowRefs = useRef(new globalThis.Map<number, HTMLButtonElement>());

  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError("");
      setNeedsLogin(false);
      setRequests([]);
      setSelectedId(null);
      setHasMore(false);
      try {
        const response = await fetch("/api/food-requests/map", { cache: "no-store", signal: controller.signal });
        if (response.status === 401) {
          if (!controller.signal.aborted) setNeedsLogin(true);
          return;
        }
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Unable to load food requests.");
        if (!Array.isArray(data.requests)) throw new Error("Unable to load food requests.");
        if (!controller.signal.aborted) {
          setRequests(data.requests);
          setHasMore(data.hasMore === true);
        }
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Unable to load food requests.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    void load();
    return () => controller.abort();
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    let layer: import("leaflet").LayerGroup | null = null;
    void import("leaflet").then((L) => {
      if (cancelled || !map) return;
      layer = L.layerGroup().addTo(map);
      const groups = new globalThis.Map<string, FoodCustomerMapRequest[]>();
      for (const item of requests) {
        if (!item.area) continue;
        const key = `${item.area.south},${item.area.west}`;
        const group = groups.get(key) ?? [];
        group.push(item);
        groups.set(key, group);
      }
      for (const group of groups.values()) {
        const area = group[0].area!;
        const active = group.some((item) => item.id === selectedId);
        const box = L.rectangle([[area.south, area.west], [area.north, area.east]], {
          color: active ? "#9a3412" : "#ea580c", weight: active ? 3 : 2, fillOpacity: active ? 0.25 : 0.12,
          bubblingMouseEvents: false,
        }).addTo(layer);
        const popup = document.createElement("div");
        const title = document.createElement("strong");
        title.textContent = `${group.length} food request${group.length === 1 ? "" : "s"} · Approximate area`;
        popup.append(title);
        const choices = document.createElement("div");
        choices.style.maxHeight = "160px";
        choices.style.overflowY = "auto";
        for (const item of group) {
          const button = document.createElement("button");
          button.type = "button";
          button.textContent = `${item.foodName} · ${item.quantity} qty`;
          button.style.display = "block";
          button.style.padding = "6px 0";
          button.addEventListener("click", () => {
            setSelectedId(item.id);
            rowRefs.current.get(item.id)?.scrollIntoView({ block: "nearest" });
          });
          choices.append(button);
        }
        popup.append(choices);
        box.bindPopup(popup);
      }
    }).catch(() => {
      if (!cancelled) setError("Map areas could not load. You can still use the request list.");
    });
    return () => { cancelled = true; layer?.remove(); };
  }, [map, requests, selectedId]);

  function select(item: FoodCustomerMapRequest) {
    setSelectedId(item.id);
    if (item.area) map?.fitBounds([[item.area.south, item.area.west], [item.area.north, item.area.east]], { maxZoom: 12, padding: [30, 30] });
  }

  return (
    <div className="col-span-2 min-w-0 space-y-2 sm:col-span-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-bold">Find Food Customers</h2>
        <div className="flex gap-3 text-xs font-semibold text-indigo-700">
          <button type="button" disabled={loading} onClick={() => setRefresh((value) => value + 1)} className="disabled:opacity-50">Refresh</button>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
      <p className="text-xs text-slate-600">Orange areas show food requests, not exact customer locations. Select a row to locate its area.</p>
      {loading && <p role="status" className="text-xs text-slate-500">Loading food requests...</p>}
      {needsLogin && <p className="text-xs"><Link href="/login" className="font-semibold text-indigo-700 underline">Login</Link> to find food customers.</p>}
      {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
      {!loading && !needsLogin && !error && requests.length === 0 && <p className="text-xs text-slate-500">No open food requests right now.</p>}
      {requests.length > 0 && <p className="text-[11px] text-slate-500">{requests.length} requests · {requests.filter((item) => item.area).length} with map areas{hasMore ? " · Latest 200 shown" : ""}</p>}
      <div aria-label="Open food customer requests" className="max-h-[min(24dvh,180px)] space-y-1 overflow-y-auto">
        {requests.map((item) => (
          <button key={item.id} type="button" ref={(element) => { if (element) rowRefs.current.set(item.id, element); else rowRefs.current.delete(item.id); }}
            onClick={() => select(item)} aria-pressed={selectedId === item.id}
            title={`${item.foodName} · ${item.mealType} · ${item.area ? "Approximate map area available" : "Map location unavailable"}`}
            className={`flex h-8 w-full min-w-0 items-center gap-2 rounded border px-2 text-left text-[11px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600 ${selectedId === item.id ? "border-orange-300 bg-orange-50" : "border-slate-100 bg-slate-50 hover:bg-orange-50"}`}>
            <span className="min-w-0 flex-1 truncate font-semibold">{item.foodName}</span>
            <span className="shrink-0">{item.quantity} qty</span>
            <span className="shrink-0">{item.budget === null ? "Budget open" : `₹${item.budget}`}</span>
            <span className="shrink-0 text-[10px]">{Number.isNaN(Date.parse(item.neededOn)) ? "" : dateFormat.format(new Date(item.neededOn))}</span>
          </button>
        ))}
      </div>
      {selected && <div className="rounded-lg border border-orange-200 bg-orange-50 p-2 text-xs">
        <p className="truncate font-semibold">{selected.foodName} · {selected.mealType} · {selected.quantity} qty</p>
        <p className="mt-1 text-slate-600">{selected.area ? "Approximate request area highlighted on the map." : "Map location unavailable. This request is still available in the list."}</p>
        <Link href={`/food/find-customers#food-request-${selected.id}`} className="mt-2 inline-block font-semibold text-indigo-700 underline">View request / Send quote →</Link>
      </div>}
    </div>
  );
}
